import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Router, NavigationEnd } from '@angular/router';
import {
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
  MsalService,
  MsalBroadcastService,
} from '@azure/msal-angular';
import {
  RedirectRequest,
  InteractionStatus,
  PopupRequest,
  InteractionType,
  AuthenticationResult,
  EventMessage,
  EventType,
} from '@azure/msal-browser';
import { AzureB2CConstants } from 'projects/shared/src/lib/constants/AzureB2CConstants';
import { EPath } from 'projects/shared/src/lib/core/enums';
import { AuthService } from 'projects/shared/src/lib/core/services/auth.service';
import { LoaderService } from 'projects/shared/src/lib/core/services/loader.service';
import { environment } from 'projects/shared/src/lib/environments/environment';
import { Subject, filter, finalize, take, takeUntil, timeout } from 'rxjs';
import { StartupService } from 'projects/shared/src/lib/core/services/startup.service';
import { isStaticStartupRoute } from 'projects/shared/src/lib/core/services/startup-failure';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly _destroying$ = new Subject<void>();
  /** Prevents recursive loginRedirect on MSAL failure events (e.g. Forgot Password). */
  private _authRecoveryInFlight = false;

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private _msalGuardConfig: MsalGuardConfiguration,
    private _msalService: MsalService,
    private _msalBroadcastService: MsalBroadcastService,
    private _router: Router,
    private _authService: AuthService,
    public loader: LoaderService,
    private _startup: StartupService,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
  ) {
    this.loadIcons(['arrow.down', 'arrow.up'], '../assets/icons');
  }

  private loadIcons(iconKeys: string[], iconUrl: string): void {
    iconKeys.forEach((key) => {
      this.matIconRegistry.addSvgIcon(
        key,
        this.domSanitizer.bypassSecurityTrustResourceUrl(
          `${iconUrl}/${key}.svg`,
        ),
      );
    });
  }

  ngOnInit(): void {
    this._handleAuthFailures();
    this._initApp();
  }

  private _handleAuthFailures(): void {
    this._msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_FAILURE,
        ),
        takeUntil(this._destroying$),
      )
      .subscribe((result: EventMessage) => {
        if (isStaticStartupRoute(this._router.url)) return;
        const errorMessage = result.error?.message ?? '';
        const errorCode = (result.error as { errorCode?: string } | null)?.errorCode ?? '';

        // Forgot Password in a B2C user flow returns AADB2C90118 — start reset policy, do not re-login.
        if (errorMessage.includes(AzureB2CConstants.ErrorRedirectResetPasswordFlow)) {
          if (this._authRecoveryInFlight) {
            return;
          }
          this._authRecoveryInFlight = true;
          this._msalBroadcastService.inProgress$
            .pipe(
              filter(
                (status: InteractionStatus) =>
                  status === InteractionStatus.None,
              ),
              take(1),
              takeUntil(this._destroying$),
            )
            .subscribe(() => {
              this._login({
                authority: environment.resetPasswordUrl,
                scopes: ['openid'],
                redirectUri: environment.redirectUri,
              });
            });
          return;
        }

        // Calling loginRedirect while an interaction is already running causes this loop.
        if (
          errorCode === 'interaction_in_progress' ||
          errorMessage.includes('interaction_in_progress')
        ) {
          console.warn('Auth failure ignored (interaction in progress):', errorMessage);
          return;
        }

        if (this._authRecoveryInFlight) {
          return;
        }

        this._startup.fail('auth-recovery', 'auth_required');
      });
  }

  private _initApp(): void {
    this._router.events.pipe(takeUntil(this._destroying$)).subscribe(event => {
      if (event instanceof NavigationEnd) this._startup.confirmNavigation(event.urlAfterRedirects);
    });
    // Error routes stay usable without the API. After Sign in again, MSAL returns
    // here (navigateToLoginRequestUrl); still complete the session and leave.
    if (isStaticStartupRoute(window.location.pathname)) {
      this.loader.setLoadingState({ global: false });
      this._msalBroadcastService.inProgress$.pipe(
        filter(status => status === InteractionStatus.None), take(1), timeout(15000),
        takeUntil(this._destroying$),
      ).subscribe({
        next: () => {
          if (this._authService.hasCachedAccount()) this._getToken();
        },
        error: () => {},
      });
      return;
    }
    this.loader.setLoadingState({ global: true });
    this._msalBroadcastService.inProgress$.pipe(
      filter(status => status === InteractionStatus.None), take(1), timeout(15000),
      takeUntil(this._destroying$),
    ).subscribe({
      next: () => {
        if (this._authRecoveryInFlight || this._startup.hasTerminalFailure) {
          this.loader.setLoadingState({ global: false });
          return;
        }
        const query = new URLSearchParams(window.location.search);
        if (query.get('error_description')?.includes(AzureB2CConstants.ErrorRedirectResetPasswordFlow)) {
          this._authRecoveryInFlight = true;
          this.loader.setLoadingState({ global: false });
          this._login({ authority: environment.resetPasswordUrl, scopes: ['openid'], redirectUri: environment.redirectUri });
          return;
        }
        const active = this._msalService.instance.getActiveAccount();
        const policy = String(active?.idTokenClaims?.['tfp'] ?? active?.idTokenClaims?.['acr'] ?? '').toLowerCase();
        if (policy === environment.policies.resetPassword.toLowerCase()) {
          this.loader.setLoadingState({ global: false });
          this._authService.logoutUser();
          return;
        }
        this._getToken();
      },
      error: error => this._startup.handleFailure(error),

    });
  }

  private _getToken(): void {
    void this._startup.ensureReady().then(ready => {
      // Router.url is still "/" while the first navigation waits on startup.
      // The address bar already has the refreshed page, so use that.
      const path = window.location.pathname.split(/[?#]/)[0];
      if (ready && !this._startup.hasTerminalFailure && this._authService.companyName &&
          (path === '/' || isStaticStartupRoute(path))) {
        void this._router.navigate([this._authService.companyName, EPath.CYBER_SECURITY]);
      }
    });
  }

  private _login(userFlowRequest?: RedirectRequest | PopupRequest): void {
    if (!userFlowRequest) {
      void this._authService.beginInteractiveSignIn().catch(error => this._startup.handleFailure(error))
        .finally(() => this.loader.setLoadingState({ global: false }));
      return;
    }
    if (this._msalGuardConfig.interactionType === InteractionType.Popup) {
      if (this._msalGuardConfig.authRequest) {
        this._msalService
          .loginPopup({
            ...this._msalGuardConfig.authRequest,
            ...userFlowRequest,
          } as PopupRequest)
          .pipe(finalize(() => (this._authRecoveryInFlight = false)))
          .subscribe({
            next: (response: AuthenticationResult) => {
              this._msalService.instance.setActiveAccount(response.account);
              this._authService.logoutUser();
            },
            error: (error: unknown) => {
              this._startup.handleFailure(error);
            },
          });
      } else {
        this._msalService
          .loginPopup(userFlowRequest)
          .pipe(finalize(() => (this._authRecoveryInFlight = false)))
          .subscribe({
            next: (response: AuthenticationResult) => {
              this._msalService.instance.setActiveAccount(response.account);
            },
            error: (error: unknown) => {
              this._startup.handleFailure(error);
            },
          });
      }
    } else {
      if (this._msalGuardConfig.authRequest) {
        this._msalService
          .loginRedirect({
            ...this._msalGuardConfig.authRequest,
            ...userFlowRequest,
          } as RedirectRequest)
          .subscribe({
            error: (error: unknown) => {
              this._authRecoveryInFlight = false;
              this._startup.handleFailure(error);
            },
          });
      } else {
        this._msalService.loginRedirect(userFlowRequest).subscribe({
          error: (error: unknown) => {
            this._authRecoveryInFlight = false;
            this._startup.handleFailure(error);
          },
        });
      }
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
